import React, { useCallback, useRef } from 'react';

import { useIdCounter } from '../../hooks';
import { getRequiredLabel } from '../../utils/internal';
import { FieldSet } from '../field-set';
import { TextInput } from '../text-input';

import { InputGroup } from './date-input.styled';
import { IDateInputProps } from './types';

type FieldsAutocomplete = { year?: string; month?: string; day?: string };
const AUTOCOMPLETE_MAP: {
  [k in NonNullable<IDateInputProps['autoComplete']>]: FieldsAutocomplete;
} = {
  bday: { year: 'bday-year', month: 'bday-month', day: 'bday-day' },
};

/**
 * Allows entry of a date via text inputs for year, month, day
 */
const DateInput: React.FC<IDateInputProps> = ({
  day,
  disabled,
  hintMessage,
  errorMessage,
  month,
  onBlur,
  onChange: onChangeProp,
  required,
  name,
  autoComplete: autoCompleteProp,
  'data-testid': testId,
  year,
  label,
  labelYear,
  labelMonth,
  labelDay,
  labelYearFormat,
  labelMonthFormat,
  labelDayFormat,
}) => {
  const messageId = useIdCounter('message');
  const inputGroupRef = useRef<HTMLDivElement>(null);
  const autoComplete: FieldsAutocomplete = AUTOCOMPLETE_MAP[autoCompleteProp!] || {};
  const onChange = useCallback<typeof onChangeProp>(
    (event) => {
      // Prevent non-numeric values from being entered
      if (!event.target.value.match(/^\d*$/)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onChangeProp(event);
    },
    [onChangeProp],
  );

  /**
   * Only emits blur if clicking away from the input group input fields.
   *
   * This is required to prevent validation while inputs are being filled.
   */
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!inputGroupRef.current?.contains(event.relatedTarget as Node)) {
        onBlur?.(event);
      }
    },
    [onBlur],
  );

  return (
    <FieldSet
      disabled={disabled}
      legend={getRequiredLabel(label, required)}
      messageId={messageId}
      errorMessage={errorMessage}
      hintMessage={hintMessage}
      data-testid={testId ? `${testId}-message` : undefined}
    >
      <InputGroup ref={inputGroupRef}>
        <TextInput
          type="text"
          maxLength={4}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={autoComplete.year}
          label={labelYear}
          aria-label={`${label} ${labelYear}`}
          name={name ? `${name}[year]` : 'year'}
          value={year}
          onChange={onChange}
          forceErrorState={!!errorMessage}
          hintMessage={!errorMessage ? labelYearFormat : undefined}
          data-testid={testId ? `${testId}-year` : undefined}
          disabled={disabled}
          onBlur={handleBlur}
          aria-describedby={messageId}
        />

        <TextInput
          type="text"
          maxLength={2}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={autoComplete.month}
          label={labelMonth}
          aria-label={`${label} ${labelMonth}`}
          name={name ? `${name}[month]` : 'month'}
          value={month}
          onChange={onChange}
          forceErrorState={!!errorMessage}
          hintMessage={!errorMessage ? labelMonthFormat : undefined}
          data-testid={testId ? `${testId}-month` : undefined}
          disabled={disabled}
          onBlur={handleBlur}
          aria-describedby={messageId}
        />

        <TextInput
          type="text"
          maxLength={2}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={autoComplete.day}
          label={labelDay}
          aria-label={`${label} ${labelDay}`}
          name={name ? `${name}[day]` : 'day'}
          value={day}
          onChange={onChange}
          forceErrorState={!!errorMessage}
          hintMessage={!errorMessage ? labelDayFormat : undefined}
          data-testid={testId ? `${testId}-day` : undefined}
          disabled={disabled}
          onBlur={handleBlur}
          aria-describedby={messageId}
        />
      </InputGroup>
    </FieldSet>
  );
};

export default DateInput;
